/**
 * ClaudeMdManager Component
 *
 * Admin component for CLAUDE.md template management:
 * - View and edit templates (base, role, agent)
 * - Push templates to agents via SSH
 * - Pull templates from agents
 * - Variable resolution for agent-specific content
 *
 * @module components/claude-templates/ClaudeMdManager
 */

import { useState, useCallback, useMemo, memo, useEffect, useRef } from 'react';
import {
  FileText,
  RefreshCw,
  Search,
  ChevronDown,
  ChevronRight,
  Loader2,
  AlertCircle,
  CheckCircle,
  Bot,
  Upload,
  Download,
  X,
  Plus,
  Trash2,
  Save,
  Eye,
  Code,
  GitBranch,
  Link2,
  Users,
  Globe,
  User,
  Lock,
  Share2,
  FolderCode,
  Home,
  Pencil,
  Bell,
  CheckCheck,
  Inbox,
} from 'lucide-react';
import MarkdownCodeEditor from '../shared/MarkdownCodeEditor';
import { useAuth } from '../../contexts/AuthContext';
import ConfirmDialog from '../common/ConfirmDialog';
import InputDialog from '../common/InputDialog';
import MarkdownRenderer from '../common/MarkdownRenderer';
import PushPreviewModal from './PushPreviewModal';
import BatchPushModal from './BatchPushModal';
import DistributeModal from './DistributeModal';
import AdminPullModal from './AdminPullModal';
import AgentEditReviewModal from './AgentEditReviewModal';
import TemplateVersionHistory from './TemplateVersionHistory';
import VariableSidebar from './VariableSidebar';
import { useWebSocket } from '../../hooks/useWebSocket';

// ============================================
// Constants
// ============================================

const API_BASE = '/api';

/**
 * Format a timestamp as relative time (e.g. "5m ago", "2h ago", "Jan 27").
 */
function formatRelativeTime(dateStr) {
  if (!dateStr) return '';
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDays = Math.floor(diffHr / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return new Date(dateStr).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// Tab definitions for ownership-based filtering
const TEMPLATE_TABS = {
  my: {
    id: 'my',
    label: 'My',
    icon: User,
    scope: 'my',
    description: 'Templates you own'
  },
  global: {
    id: 'global',
    label: 'Global',
    icon: Globe,
    scope: 'global',
    description: 'Shared base and role templates'
  },
  admin: {
    id: 'admin',
    label: 'Admin',
    icon: Users,
    scope: 'all',
    description: 'Admin view - all templates',
    adminOnly: true
  },
  changes: {
    id: 'changes',
    label: 'Changes',
    icon: Bell,
    description: 'Recent template changes by agents'
  }
};

// Location options for CLAUDE.md deployment target
const LOCATION_OPTIONS = {
  global: {
    id: 'global',
    label: 'Global',
    icon: Home,
    path: '~/.claude/CLAUDE.md',
    description: 'User-wide CLAUDE.md in home directory'
  },
  project: {
    id: 'project',
    label: 'Project',
    icon: FolderCode,
    path: 'WORKING_DIR/.claude/CLAUDE.md',
    description: 'Project-specific CLAUDE.md in working directory'
  }
};

const TEMPLATE_TYPE_COLORS = {
  base: {
    bg: 'bg-blue-500/20',
    text: 'text-blue-400',
    border: 'border-blue-500/30',
    label: 'Base'
  },
  role: {
    bg: 'bg-green-500/20',
    text: 'text-green-400',
    border: 'border-green-500/30',
    label: 'Role'
  },
  agent: {
    bg: 'bg-purple-500/20',
    text: 'text-purple-400',
    border: 'border-purple-500/30',
    label: 'Agent'
  }
};

// ============================================
// API Functions
// ============================================

async function fetchTemplates(scope = 'my') {
  const res = await fetch(`${API_BASE}/claude-templates?scope=${scope}`, { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to fetch templates');
  return res.json();
}

async function fetchAgents() {
  const res = await fetch(`${API_BASE}/agents?status=active`, { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to fetch agents');
  return res.json();
}

async function createTemplate(data) {
  const res = await fetch(`${API_BASE}/claude-templates`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data)
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.message || 'Failed to create template');
  }
  return res.json();
}

async function updateTemplate(id, data) {
  const res = await fetch(`${API_BASE}/claude-templates/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data)
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.message || 'Failed to update template');
  }
  return res.json();
}

async function deleteTemplate(id) {
  const res = await fetch(`${API_BASE}/claude-templates/${id}`, {
    method: 'DELETE',
    credentials: 'include'
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.message || 'Failed to delete template');
  }
  return { success: true };
}

async function pushToAgent(agentId, templateId) {
  const res = await fetch(`${API_BASE}/claude-templates/agents/${agentId}/claude-md`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ templateId })
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.message || 'Failed to push template to agent');
  }
  return res.json();
}

async function pullFromAgent(agentId, location = 'global') {
  const res = await fetch(`${API_BASE}/claude-templates/agents/${agentId}/claude-md?location=${location}`, {
    credentials: 'include'
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.message || 'Failed to pull from agent');
  }
  return res.json();
}

async function importFromAgent(agentId, location = 'global') {
  const res = await fetch(`${API_BASE}/claude-templates/import/${agentId}?location=${location}`, {
    method: 'POST',
    credentials: 'include'
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.message || 'Failed to import from agent');
  }
  return res.json();
}

// ============================================
// Sub-Components
// ============================================

const TemplateTypeBadge = memo(function TemplateTypeBadge({ type }) {
  const colors = TEMPLATE_TYPE_COLORS[type] || TEMPLATE_TYPE_COLORS.base;

  return (
    <span
      className={`
        inline-flex items-center px-2 py-0.5 text-xs font-medium rounded
        ${colors.bg} ${colors.text} ${colors.border} border
      `}
    >
      {colors.label}
    </span>
  );
});

/**
 * LocationBadge - Shows template deployment location (global or project)
 */
const LocationBadge = memo(function LocationBadge({ location }) {
  const isProject = location === 'project';

  return (
    <span
      className={`
        inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded border
        ${isProject
          ? 'bg-orange-500/20 text-orange-400 border-orange-500/30'
          : 'bg-sky-500/20 text-sky-400 border-sky-500/30'
        }
      `}
      title={isProject
        ? 'Project CLAUDE.md (WORKING_DIR/.claude/CLAUDE.md)'
        : 'Global CLAUDE.md (~/.claude/CLAUDE.md)'
      }
    >
      {isProject ? (
        <FolderCode className="w-3 h-3" />
      ) : (
        <Home className="w-3 h-3" />
      )}
      {isProject ? 'Project' : 'Global'}
    </span>
  );
});

/**
 * CreateTemplateDialog - Dialog for creating new templates with name and location
 */
const CreateTemplateDialog = memo(function CreateTemplateDialog({
  isOpen,
  type,
  location,
  onLocationChange,
  onConfirm,
  onCancel,
  isProcessing
}) {
  const [name, setName] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setName('');
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && name.trim() && !isProcessing) {
      onConfirm(name.trim(), location);
    } else if (e.key === 'Escape') {
      onCancel();
    }
  };

  if (!isOpen) return null;

  const typeLabel = type ? type.charAt(0).toUpperCase() + type.slice(1) : '';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-gray-900 border border-gray-700 rounded-xl shadow-2xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-700">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Plus className="w-5 h-5 text-indigo-400" />
            Create {typeLabel} Template
          </h3>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-4">
          {/* Name Input */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">
              Template Name
            </label>
            <input
              ref={inputRef}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="template-name"
              disabled={isProcessing}
              className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:opacity-50"
            />
          </div>

          {/* Location Selector */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">
              Deployment Location
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => onLocationChange('global')}
                disabled={isProcessing}
                className={`
                  flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border transition-colors
                  ${location === 'global'
                    ? 'bg-sky-500/20 border-sky-500/50 text-sky-400'
                    : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-600'
                  }
                  disabled:opacity-50
                `}
              >
                <Home className="w-4 h-4" />
                <span>Global</span>
              </button>
              <button
                type="button"
                onClick={() => onLocationChange('project')}
                disabled={isProcessing}
                className={`
                  flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border transition-colors
                  ${location === 'project'
                    ? 'bg-orange-500/20 border-orange-500/50 text-orange-400'
                    : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-600'
                  }
                  disabled:opacity-50
                `}
              >
                <FolderCode className="w-4 h-4" />
                <span>Project</span>
              </button>
            </div>
            <p className="mt-2 text-xs text-gray-500">
              {location === 'project'
                ? 'Deploys to WORKING_DIR/.claude/CLAUDE.md'
                : 'Deploys to ~/.claude/CLAUDE.md'
              }
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="px-6 py-4 border-t border-gray-700 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={isProcessing}
            className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => name.trim() && onConfirm(name.trim(), location)}
            disabled={isProcessing || !name.trim()}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-500 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isProcessing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Plus className="w-4 h-4" />
            )}
            Create
          </button>
        </div>
      </div>
    </div>
  );
});

const StatusMessage = memo(function StatusMessage({ type, message, onDismiss }) {
  const isError = type === 'error';

  return (
    <div
      className={`
        flex items-center justify-between p-3 rounded-lg
        ${isError ? 'bg-red-500/10 border border-red-500/30' : 'bg-green-500/10 border border-green-500/30'}
      `}
    >
      <div className="flex items-center gap-2">
        {isError ? (
          <AlertCircle className="w-5 h-5 text-red-400" />
        ) : (
          <CheckCircle className="w-5 h-5 text-green-400" />
        )}
        <span className={isError ? 'text-red-300' : 'text-green-300'}>
          {message}
        </span>
      </div>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          className="p-1 hover:bg-gray-700 rounded transition-colors"
        >
          <X className="w-4 h-4 text-gray-400" />
        </button>
      )}
    </div>
  );
});

const AgentSelector = memo(function AgentSelector({
  agents,
  selectedAgent,
  onSelect,
  isLoading
}) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        disabled={isLoading}
        className={`
          flex items-center justify-between w-64 px-4 py-2.5
          bg-gray-800 border border-gray-700 rounded-lg
          text-left text-gray-200
          hover:border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500
          disabled:opacity-50 disabled:cursor-not-allowed
          transition-colors
        `}
      >
        <div className="flex items-center gap-2">
          <Bot className="w-4 h-4 text-gray-400" />
          <span className="truncate">
            {selectedAgent?.displayName || selectedAgent?.name || 'Select Agent'}
          </span>
        </div>
        {isLoading ? (
          <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
        ) : (
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        )}
      </button>

      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-gray-800 border border-gray-700 rounded-lg shadow-xl overflow-hidden">
          <div className="max-h-60 overflow-y-auto">
            {agents.length === 0 ? (
              <div className="px-4 py-3 text-sm text-gray-500">
                No active agents found
              </div>
            ) : (
              agents.map((agent) => (
                <button
                  key={agent.id}
                  type="button"
                  onClick={() => {
                    onSelect(agent);
                    setIsOpen(false);
                  }}
                  className={`
                    w-full px-4 py-2.5 text-left text-sm
                    ${selectedAgent?.id === agent.id
                      ? 'bg-indigo-600/30 text-white'
                      : 'text-gray-300 hover:bg-gray-700'
                    }
                    transition-colors
                  `}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Bot className="w-4 h-4 text-gray-400" />
                      <span>{agent.displayName || agent.name}</span>
                    </div>
                    <span className="text-xs text-gray-500">{agent.host?.hostname}</span>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
});

/**
 * TemplateTabs - Tab navigation for ownership-based filtering
 */
const TemplateTabs = memo(function TemplateTabs({
  activeTab,
  onTabChange,
  isAdmin,
  templateCounts,
  unreadChangesCount
}) {
  const tabs = useMemo(() => {
    const available = [TEMPLATE_TABS.my, TEMPLATE_TABS.global];
    if (isAdmin) {
      available.push(TEMPLATE_TABS.admin);
    }
    available.push(TEMPLATE_TABS.changes);
    return available;
  }, [isAdmin]);

  return (
    <div className="flex border-b border-gray-700 w-full overflow-hidden">
      {tabs.map((tab) => {
        const Icon = tab.icon;
        const isActive = activeTab === tab.id;
        const count = tab.id === 'changes'
          ? (unreadChangesCount || 0)
          : (templateCounts?.[tab.id] || 0);

        return (
          <button
            key={tab.id}
            type="button"
            onClick={() => onTabChange(tab.id)}
            className={`
              flex-1 flex items-center justify-center gap-1 px-2 py-2.5 text-sm font-medium whitespace-nowrap
              border-b-2 -mb-px transition-colors min-w-0
              ${isActive
                ? 'border-indigo-500 text-indigo-400'
                : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-600'
              }
            `}
            title={tab.description}
          >
            <Icon className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">{tab.label}</span>
            {count > 0 && (
              <span className={`
                px-1.5 py-0.5 text-xs rounded-full flex-shrink-0
                ${tab.id === 'changes' && !isActive
                  ? 'bg-orange-500/20 text-orange-300'
                  : isActive ? 'bg-indigo-500/20 text-indigo-300' : 'bg-gray-700 text-gray-400'
                }
              `}>
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
});

/**
 * ChangesList — Shows recent template changes by agents.
 * Displays unread/read state with Mark All as Read action.
 */
const ChangesList = memo(function ChangesList({
  changes,
  isLoading,
  onMarkAllRead,
  onSelectChange,
  isMarkingRead,
}) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-indigo-400 animate-spin" />
      </div>
    );
  }

  if (changes.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-gray-500">
        <Inbox className="w-10 h-10 mb-3 text-gray-600" />
        <p className="text-sm font-medium">No recent changes</p>
        <p className="text-xs mt-1">Agent edits will appear here</p>
      </div>
    );
  }

  const unreadCount = changes.filter(c => !c.readAt).length;

  return (
    <div className="flex flex-col h-full">
      {/* Header with Mark All Read */}
      {unreadCount > 0 && (
        <div className="px-4 py-2 border-b border-gray-800">
          <button
            type="button"
            onClick={onMarkAllRead}
            disabled={isMarkingRead}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-indigo-400 bg-indigo-500/10 hover:bg-indigo-500/20 rounded-lg transition-colors disabled:opacity-50"
          >
            {isMarkingRead ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <CheckCheck className="w-3 h-3" />
            )}
            Mark all as read ({unreadCount})
          </button>
        </div>
      )}

      {/* Changes list */}
      <div className="flex-1 overflow-y-auto p-2 space-y-1">
        {changes.map((change) => {
          const isUnread = !change.readAt;
          return (
            <button
              key={change.id}
              type="button"
              onClick={() => onSelectChange?.(change)}
              className={`
                w-full text-left px-3 py-2.5 rounded-lg text-sm transition-colors
                ${isUnread
                  ? 'bg-indigo-600/10 border border-indigo-500/20 hover:bg-indigo-600/20'
                  : 'bg-gray-800/30 border border-gray-700/50 hover:bg-gray-800/50'
                }
              `}
            >
              <div className="flex items-center gap-2">
                {isUnread && (
                  <span className="w-2 h-2 rounded-full bg-orange-400 flex-shrink-0" />
                )}
                <span className={`font-medium truncate ${isUnread ? 'text-gray-200' : 'text-gray-400'}`}>
                  {change.templateName || 'Unknown template'}
                </span>
              </div>
              <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                <Bot className="w-3 h-3" />
                <span className="truncate">
                  {change.createdByName || 'Agent'}
                  {change.targetAgent?.displayName && (
                    <> &rarr; {change.targetAgent.displayName}</>
                  )}
                </span>
                <span>&middot;</span>
                <span>{change.action || 'edited'}</span>
                {change.createdAt && (
                  <>
                    <span>&middot;</span>
                    <span>{formatRelativeTime(change.createdAt)}</span>
                  </>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
});

const TemplateListItem = memo(function TemplateListItem({
  template,
  isSelected,
  onSelect,
  onDelete,
  onRename,
  isProcessing,
  isReadOnly,
  pendingEditCount = 0
}) {
  return (
    <div
      className={`
        flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors
        ${isSelected
          ? 'bg-indigo-600/20 border-indigo-500/50'
          : 'bg-gray-800/50 border-gray-700/50 hover:border-gray-600'
        }
      `}
      onClick={() => onSelect(template)}
    >
      {/* Icon - fixed width */}
      <FileText className="w-5 h-5 text-gray-400 flex-shrink-0" />

      {/* Name and badges - stacked layout for narrow containers */}
      <div className="flex-1 min-w-0 overflow-hidden">
        <div className="flex items-center gap-1.5">
          <span
            className="font-medium text-gray-200 truncate"
            title={template.name}
          >
            {template.name}
          </span>
          {template.parentId && (
            <span className="text-xs text-gray-500 flex-shrink-0">(inherits)</span>
          )}
          {template.isGlobal && (
            <span className="text-xs text-blue-400 flex-shrink-0">(global)</span>
          )}
        </div>
        {/* Badges below name */}
        <div className="flex items-center gap-1.5 mt-1">
          <TemplateTypeBadge type={template.type} />
          <LocationBadge location={template.location || 'global'} />
          {pendingEditCount > 0 && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium text-orange-300 bg-orange-500/20 border border-orange-500/30 rounded-full">
              <Pencil className="w-2.5 h-2.5" />
              {pendingEditCount}
            </span>
          )}
        </div>
      </div>

      {/* Actions - fixed width */}
      <div className="flex items-center gap-0.5 flex-shrink-0">
        {!isReadOnly && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRename(template);
            }}
            disabled={isProcessing}
            className="p-1.5 text-gray-400 hover:text-indigo-400 hover:bg-indigo-500/10 rounded transition-colors disabled:opacity-50"
            title="Rename template"
          >
            <Pencil className="w-4 h-4" />
          </button>
        )}
        {isReadOnly ? (
          <span className="p-1.5 text-gray-500" title="Read-only (Admin only)">
            <Lock className="w-4 h-4" />
          </span>
        ) : (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(template.id);
            }}
            disabled={isProcessing}
            className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors disabled:opacity-50"
            title="Delete template"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
});

const TemplateList = memo(function TemplateList({
  templates,
  selectedTemplate,
  onSelect,
  onDelete,
  onRename,
  searchQuery,
  isProcessing,
  isReadOnly,
  pendingEditsByTemplate = {}
}) {
  // Group templates by type
  const groupedTemplates = useMemo(() => {
    const filtered = templates.filter(t =>
      !searchQuery || t.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return {
      base: filtered.filter(t => t.type === 'base'),
      role: filtered.filter(t => t.type === 'role'),
      agent: filtered.filter(t => t.type === 'agent'),
    };
  }, [templates, searchQuery]);

  const [expandedGroups, setExpandedGroups] = useState({ base: true, role: true, agent: true });

  const toggleGroup = (group) => {
    setExpandedGroups(prev => ({ ...prev, [group]: !prev[group] }));
  };

  const renderGroup = (type, items) => {
    if (items.length === 0) return null;
    const colors = TEMPLATE_TYPE_COLORS[type];

    return (
      <div key={type} className="mb-4">
        <button
          type="button"
          onClick={() => toggleGroup(type)}
          className="flex items-center gap-2 w-full text-left mb-2"
        >
          {expandedGroups[type] ? (
            <ChevronDown className="w-4 h-4 text-gray-400" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-400" />
          )}
          <span className={`text-sm font-medium uppercase tracking-wider ${colors.text}`}>
            {colors.label} Templates ({items.length})
          </span>
        </button>
        {expandedGroups[type] && (
          <div className="space-y-2 ml-6">
            {items.map(template => (
              <TemplateListItem
                key={template.id}
                template={template}
                isSelected={selectedTemplate?.id === template.id}
                onSelect={onSelect}
                onDelete={onDelete}
                onRename={onRename}
                isProcessing={isProcessing}
                isReadOnly={isReadOnly}
                pendingEditCount={pendingEditsByTemplate[template.id] || 0}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-2">
      {renderGroup('base', groupedTemplates.base)}
      {renderGroup('role', groupedTemplates.role)}
      {renderGroup('agent', groupedTemplates.agent)}

      {templates.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No templates found</p>
        </div>
      )}
    </div>
  );
});

/**
 * ParentSelector - Dropdown to select parent template for inheritance
 */
const ParentSelector = memo(function ParentSelector({
  templates,
  currentTemplate,
  selectedParentId,
  onSelect,
  isDisabled
}) {
  const [isOpen, setIsOpen] = useState(false);

  // Filter valid parents: must be different type hierarchy
  // base templates have no parent, role inherits from base, agent inherits from base or role
  const validParents = useMemo(() => {
    if (!currentTemplate) return [];

    const { type, id } = currentTemplate;

    // Base templates cannot have parents
    if (type === 'base') return [];

    // Role templates can only inherit from base
    if (type === 'role') {
      return templates.filter(t => t.type === 'base' && t.id !== id);
    }

    // Agent templates can inherit from base or role
    if (type === 'agent') {
      return templates.filter(t => (t.type === 'base' || t.type === 'role') && t.id !== id);
    }

    return [];
  }, [templates, currentTemplate]);

  const selectedParent = templates.find(t => t.id === selectedParentId);

  if (!currentTemplate || currentTemplate.type === 'base') {
    return null;
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        disabled={isDisabled}
        className={`
          flex items-center justify-between w-full px-3 py-2
          bg-gray-800 border border-gray-700 rounded-lg
          text-left text-sm text-gray-200
          hover:border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500
          disabled:opacity-50 disabled:cursor-not-allowed
          transition-colors
        `}
      >
        <div className="flex items-center gap-2">
          <GitBranch className="w-4 h-4 text-gray-400" />
          <span className="truncate">
            {selectedParent ? (
              <span className="flex items-center gap-2">
                <span>{selectedParent.name}</span>
                <TemplateTypeBadge type={selectedParent.type} />
              </span>
            ) : (
              <span className="text-gray-500">No parent (standalone)</span>
            )}
          </span>
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-gray-800 border border-gray-700 rounded-lg shadow-xl overflow-hidden">
          <div className="max-h-48 overflow-y-auto">
            {/* No parent option */}
            <button
              type="button"
              onClick={() => {
                onSelect(null);
                setIsOpen(false);
              }}
              className={`
                w-full px-3 py-2 text-left text-sm
                ${!selectedParentId
                  ? 'bg-indigo-600/30 text-white'
                  : 'text-gray-300 hover:bg-gray-700'
                }
                transition-colors
              `}
            >
              <span className="text-gray-500">No parent (standalone)</span>
            </button>

            {validParents.map((parent) => (
              <button
                key={parent.id}
                type="button"
                onClick={() => {
                  onSelect(parent.id);
                  setIsOpen(false);
                }}
                className={`
                  w-full px-3 py-2 text-left text-sm
                  ${selectedParentId === parent.id
                    ? 'bg-indigo-600/30 text-white'
                    : 'text-gray-300 hover:bg-gray-700'
                  }
                  transition-colors
                `}
              >
                <div className="flex items-center justify-between">
                  <span>{parent.name}</span>
                  <TemplateTypeBadge type={parent.type} />
                </div>
              </button>
            ))}

            {validParents.length === 0 && (
              <div className="px-3 py-2 text-sm text-gray-500">
                No valid parents available
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
});

const TemplateEditor = memo(function TemplateEditor({
  template,
  templates,
  onChange,
  onSave,
  onParentChange,
  onVersionRollback,
  isSaving,
  hasChanges
}) {
  // View mode: 'edit' | 'preview'
  const [showPreview, setShowPreview] = useState(false);
  const [previewContent, setPreviewContent] = useState(null); // null = use template.content
  const insertTextRef = useRef(null);

  // Register insert function from MarkdownCodeEditor via onInsertText callback
  const handleInsertTextReady = useCallback((insertFn) => {
    insertTextRef.current = insertFn;
  }, []);

  // Insert variable at cursor position via CM6 API
  const handleVariableInsert = useCallback((varString) => {
    if (!template) return;

    if (insertTextRef.current) {
      insertTextRef.current(varString);
    } else {
      // Fallback: append at the end
      const content = template.content || '';
      onChange({ ...template, content: content + varString });
    }
  }, [template, onChange]);

  if (!template) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500">
        <div className="text-center">
          <FileText className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <p>Select a template to edit</p>
        </div>
      </div>
    );
  }

  // Find parent template for display
  const parentTemplate = template.parentId
    ? templates.find(t => t.id === template.parentId)
    : null;


  return (
    <div className="flex flex-col h-full">
      {/* Editor Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-700">
        <div className="flex items-center gap-3">
          <FileText className="w-5 h-5 text-indigo-400" />
          <span className="font-medium text-white">{template.name}</span>
          <TemplateTypeBadge type={template.type} />
          {hasChanges && (
            <span className="text-xs text-yellow-400">(unsaved changes)</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* View Mode Toggle: Edit / Preview */}
          <div className="flex items-center bg-gray-800 rounded-lg p-0.5">
            <button
              type="button"
              onClick={() => {
                // If viewing a historical version, load it into editor
                if (previewContent !== null) {
                  onChange?.({ ...template, content: previewContent });
                }
                setShowPreview(false);
                setPreviewContent(null);
              }}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                !showPreview
                  ? 'bg-gray-700 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
              title="Edit mode"
            >
              <Code className="w-4 h-4" />
              Edit
            </button>
            <button
              type="button"
              onClick={() => setShowPreview(true)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                showPreview
                  ? 'bg-gray-700 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
              title="Preview mode"
            >
              <Eye className="w-4 h-4" />
              Preview
            </button>
          </div>
          {/* Save Button */}
          <button
            type="button"
            onClick={onSave}
            disabled={isSaving || !hasChanges}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-500 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSaving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Save
          </button>
        </div>
      </div>

      {/* Inheritance Bar - only shown for role and agent templates */}
      {template.type !== 'base' && (
        <div className="px-4 py-3 bg-gray-800/50 border-b border-gray-700">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <Link2 className="w-4 h-4" />
              <span>Inherits from:</span>
            </div>
            <div className="w-64">
              <ParentSelector
                templates={templates}
                currentTemplate={template}
                selectedParentId={template.parentId}
                onSelect={onParentChange}
                isDisabled={isSaving}
              />
            </div>
            {parentTemplate && (
              <span className="text-xs text-gray-500">
                Content will be merged: Parent → This template
              </span>
            )}
          </div>
        </div>
      )}

      {/* Editor Body with Sidebar */}
      <div className="flex-1 flex overflow-hidden">
        {/* Main Editor / Preview */}
        {showPreview ? (
          <div className="flex-1 p-4 overflow-auto">
            {previewContent !== null && (
              <div className="mb-2 px-3 py-1.5 text-xs text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                Viewing historical version — click Edit to load into editor
              </div>
            )}
            <div className="p-4 bg-gray-900 border border-gray-700 rounded-lg">
              <MarkdownRenderer content={previewContent ?? template.content ?? ''} />
            </div>
          </div>
        ) : (
          <div className="flex-1 relative">
            <div className="absolute inset-4">
              <MarkdownCodeEditor
                value={template.content || ''}
                onChange={(value) => onChange({ ...template, content: value || '' })}
                placeholder="Enter template content..."
                onInsertText={handleInsertTextReady}
                height="100%"
              />
            </div>
          </div>
        )}

        {/* Variable Sidebar - hidden in preview mode */}
        {!showPreview && (
          <VariableSidebar
            onInsert={handleVariableInsert}
            className="w-56 flex-shrink-0"
          />
        )}
      </div>

      {/* Version History */}
      <TemplateVersionHistory
        templateId={template?.id}
        onRollback={(restoredData) => {
          // Update editor with restored content
          if (restoredData?.content !== undefined) {
            onChange?.({ ...template, content: restoredData.content });
          }
          onVersionRollback?.(restoredData);
        }}
        onPreview={(versionData) => {
          if (versionData?.content !== undefined) {
            // Set preview content for Preview mode
            setPreviewContent(versionData.content);
            // In Edit mode: load version content into editor
            if (!showPreview) {
              onChange?.({ ...template, content: versionData.content });
            }
          }
        }}
      />
    </div>
  );
});

// ============================================
// Main Component
// ============================================

export default function ClaudeMdManager({ className = '' }) {
  // Auth context
  const { isAdmin, isAuthenticated } = useAuth();

  // State
  const [templates, setTemplates] = useState([]);
  const [agents, setAgents] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editedTemplate, setEditedTemplate] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('my');
  const [templateCounts, setTemplateCounts] = useState({ my: 0, global: 0, admin: 0 });

  const [isLoadingTemplates, setIsLoadingTemplates] = useState(true);
  const [isLoadingAgents, setIsLoadingAgents] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [statusMessage, setStatusMessage] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, template: null });
  const [renameDialog, setRenameDialog] = useState({ isOpen: false, template: null });
  const [createDialog, setCreateDialog] = useState({ isOpen: false, type: null, location: 'global' });
  const [pushPreview, setPushPreview] = useState({ isOpen: false });
  const [batchPush, setBatchPush] = useState({ isOpen: false });
  const [distributeModal, setDistributeModal] = useState({ isOpen: false });
  const [adminPullModal, setAdminPullModal] = useState({ isOpen: false });
  const [pullLocation, setPullLocation] = useState('global');

  // Pending Agent Edits
  const [pendingEdits, setPendingEdits] = useState([]);
  const [pendingEditsCount, setPendingEditsCount] = useState(0);
  const [editReviewModal, setEditReviewModal] = useState({ isOpen: false, currentIndex: 0 });

  // Recent Changes
  const [recentChanges, setRecentChanges] = useState([]);
  const [isLoadingChanges, setIsLoadingChanges] = useState(false);
  const [isMarkingRead, setIsMarkingRead] = useState(false);
  const [isLoadingChangeTemplate, setIsLoadingChangeTemplate] = useState(false);
  const [changeTemplateError, setChangeTemplateError] = useState(null);
  const changeRequestRef = useRef(0);
  const unreadChangesCount = useMemo(() => recentChanges.filter(c => !c.readAt).length, [recentChanges]);

  // Group pending edits by templateId for badge display in TemplateList
  const pendingEditsByTemplate = useMemo(() => {
    const map = {};
    for (const edit of pendingEdits) {
      const tid = edit.template?.id || edit.templateId;
      if (tid) {
        map[tid] = (map[tid] || 0) + 1;
      }
    }
    return map;
  }, [pendingEdits]);

  // Determine if current tab is read-only for non-admin users
  // Global (shared) templates are read-only for non-admins
  const isCurrentTabReadOnly = useMemo(() => {
    if (activeTab === 'global' && !isAdmin) return true;
    return false;
  }, [activeTab, isAdmin]);

  // API Handlers — loadTemplates must be defined before handlers that reference it (TDZ fix)
  const loadTemplates = useCallback(async (tab = activeTab) => {
    setIsLoadingTemplates(true);
    try {
      const scope = TEMPLATE_TABS[tab]?.scope || 'my';
      const result = await fetchTemplates(scope);
      setTemplates(result.data || []);

      // Update counts if provided by API
      if (result.byOwnership) {
        setTemplateCounts({
          my: result.byOwnership.my?.length || 0,
          global: result.byOwnership.global?.length || 0,
          admin: result.total || 0
        });
      }
    } catch (err) {
      setStatusMessage({ type: 'error', message: err.message });
    } finally {
      setIsLoadingTemplates(false);
    }
  }, [activeTab]);

  // Fetch pending agent edits
  const loadPendingEdits = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/claude-templates/pending-edits?status=pending`, {
        credentials: 'include',
      });
      if (!res.ok) return;
      const result = await res.json();
      setPendingEdits(result.data || []);
      setPendingEditsCount(result.totalPending || 0);
    } catch {
      // Silent fail — badge just won't show
    }
  }, []);

  // Pending edit review handlers
  const handleEditReviewOpen = useCallback(() => {
    if (pendingEdits.length > 0) {
      setEditReviewModal({ isOpen: true, currentIndex: 0 });
    }
  }, [pendingEdits]);

  const handleEditReviewClose = useCallback(() => {
    setEditReviewModal({ isOpen: false, currentIndex: 0 });
  }, []);

  const handleEditReviewNavigate = useCallback((index) => {
    setEditReviewModal(prev => ({ ...prev, currentIndex: index }));
  }, []);

  const handleEditApprove = useCallback((edit) => {
    setPendingEdits(prev => prev.filter(e => e.id !== edit.id));
    setPendingEditsCount(prev => Math.max(0, prev - 1));
    setStatusMessage({ type: 'success', message: `Edit approved — template updated for ${edit.targetAgent?.displayName || edit.targetAgent?.name}` });
    // If no more edits, close modal; else adjust index
    setPendingEdits(prev => {
      if (prev.length === 0) {
        setEditReviewModal({ isOpen: false, currentIndex: 0 });
      } else {
        setEditReviewModal(m => ({
          ...m,
          currentIndex: Math.min(m.currentIndex, prev.length - 1),
        }));
      }
      return prev;
    });
    // Reload templates to reflect changes
    loadTemplates(activeTab);
  }, [activeTab, loadTemplates]);

  const handleEditReject = useCallback((edit) => {
    setPendingEdits(prev => prev.filter(e => e.id !== edit.id));
    setPendingEditsCount(prev => Math.max(0, prev - 1));
    setStatusMessage({ type: 'success', message: `Edit rejected` });
    setPendingEdits(prev => {
      if (prev.length === 0) {
        setEditReviewModal({ isOpen: false, currentIndex: 0 });
      } else {
        setEditReviewModal(m => ({
          ...m,
          currentIndex: Math.min(m.currentIndex, prev.length - 1),
        }));
      }
      return prev;
    });
  }, []);

  // WebSocket subscription
  const { subscribe } = useWebSocket();

  // Load data on mount
  useEffect(() => {
    loadAgents();
    loadPendingEdits();
  }, []);

  // Subscribe to real-time pending-edit events
  useEffect(() => {
    const cleanupPending = subscribe('template:pending-edit', () => {
      loadPendingEdits();
    });
    const cleanupResolved = subscribe('template:edit-resolved', () => {
      loadPendingEdits();
    });
    return () => {
      cleanupPending?.();
      cleanupResolved?.();
    };
  }, [subscribe, loadPendingEdits]);

  // Load recent changes from API — must be defined before useEffect that references it (TDZ fix)
  const loadChanges = useCallback(async () => {
    if (!isAuthenticated) return;
    setIsLoadingChanges(true);
    try {
      const res = await fetch(`${API_BASE}/claude-templates/pending-edits/changes`, {
        credentials: 'include',
      });
      if (!res.ok) return;
      const result = await res.json();
      setRecentChanges(result.data || []);
    } catch {
      // Silent fail
    } finally {
      setIsLoadingChanges(false);
    }
  }, [isAuthenticated]);

  // Reload templates/changes when tab changes
  useEffect(() => {
    if (activeTab === 'changes') {
      loadChanges();
    } else {
      loadTemplates(activeTab);
    }
  }, [activeTab, loadChanges]);

  // Sync edited template when selection changes
  useEffect(() => {
    setEditedTemplate(selectedTemplate ? { ...selectedTemplate } : null);
  }, [selectedTemplate]);

  // Check for unsaved changes
  const hasChanges = useMemo(() => {
    if (!selectedTemplate || !editedTemplate) return false;
    return selectedTemplate.content !== editedTemplate.content ||
           selectedTemplate.parentId !== editedTemplate.parentId;
  }, [selectedTemplate, editedTemplate]);

  // Handle tab change
  const handleTabChange = useCallback((tabId) => {
    setActiveTab(tabId);
    setSelectedTemplate(null); // Clear selection when switching tabs
  }, []);

  const handleMarkAllRead = useCallback(async () => {
    setIsMarkingRead(true);
    const snapshot = recentChanges;
    // Optimistic update
    setRecentChanges(prev => prev.map(c => ({ ...c, readAt: c.readAt || new Date().toISOString() })));
    try {
      const res = await fetch(`${API_BASE}/claude-templates/pending-edits/changes/read-all`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) {
        setRecentChanges(snapshot); // Revert on failure
      }
    } catch {
      setRecentChanges(snapshot); // Revert on error
    } finally {
      setIsMarkingRead(false);
    }
  }, [recentChanges]);

  const handleSelectChange = useCallback(async (change) => {
    // Mark as read (optimistic)
    if (!change.readAt) {
      setRecentChanges(prev => prev.map(c =>
        c.id === change.id ? { ...c, readAt: new Date().toISOString() } : c
      ));
      try {
        const res = await fetch(`${API_BASE}/claude-templates/pending-edits/changes/${change.versionId || change.id}/read`, {
          method: 'POST',
          credentials: 'include',
        });
        if (!res.ok) {
          setRecentChanges(prev => prev.map(c =>
            c.id === change.id ? { ...c, readAt: null } : c
          ));
        }
      } catch {
        setRecentChanges(prev => prev.map(c =>
          c.id === change.id ? { ...c, readAt: null } : c
        ));
      }
    }

    // Load the associated template in the right panel
    const templateId = change.templateId || change.template?.id;
    if (templateId) {
      const requestId = ++changeRequestRef.current;
      setIsLoadingChangeTemplate(true);
      setChangeTemplateError(null);
      try {
        const res = await fetch(`${API_BASE}/claude-templates/${templateId}`, {
          credentials: 'include',
        });
        if (requestId !== changeRequestRef.current) return; // stale request
        if (!res.ok) {
          setChangeTemplateError('Template could not be loaded');
          setSelectedTemplate(null);
          return;
        }
        const template = await res.json();
        setSelectedTemplate(template.data || template);
      } catch {
        if (requestId !== changeRequestRef.current) return;
        setChangeTemplateError('Template could not be loaded');
        setSelectedTemplate(null);
      } finally {
        if (requestId === changeRequestRef.current) {
          setIsLoadingChangeTemplate(false);
        }
      }
    }
  }, []);

  const loadAgents = useCallback(async () => {
    setIsLoadingAgents(true);
    try {
      const result = await fetchAgents();
      setAgents(result.data || []);
    } catch (err) {
      setStatusMessage({ type: 'error', message: 'Failed to load agents' });
      setAgents([]);
    } finally {
      setIsLoadingAgents(false);
    }
  }, []);

  const handleSave = useCallback(async () => {
    if (!editedTemplate) return;
    setIsSaving(true);
    try {
      await updateTemplate(editedTemplate.id, editedTemplate);
      setSelectedTemplate(editedTemplate);
      setTemplates(prev => prev.map(t => t.id === editedTemplate.id ? editedTemplate : t));
      setStatusMessage({ type: 'success', message: 'Template saved successfully' });
    } catch (err) {
      setStatusMessage({ type: 'error', message: err.message });
    } finally {
      setIsSaving(false);
    }
  }, [editedTemplate]);

  const handleDeleteClick = useCallback((templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setDeleteConfirm({ isOpen: true, template });
    }
  }, [templates]);

  const handleDeleteConfirm = useCallback(async () => {
    const templateId = deleteConfirm.template?.id;
    if (!templateId) return;

    setIsProcessing(true);
    try {
      await deleteTemplate(templateId);
      setTemplates(prev => prev.filter(t => t.id !== templateId));
      if (selectedTemplate?.id === templateId) {
        setSelectedTemplate(null);
      }
      setStatusMessage({ type: 'success', message: 'Template deleted' });
      setDeleteConfirm({ isOpen: false, template: null });
    } catch (err) {
      setStatusMessage({ type: 'error', message: err.message });
    } finally {
      setIsProcessing(false);
    }
  }, [deleteConfirm.template, selectedTemplate]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteConfirm({ isOpen: false, template: null });
  }, []);

  const handleRenameClick = useCallback((template) => {
    setRenameDialog({ isOpen: true, template });
  }, []);

  const handleRenameConfirm = useCallback(async (newName) => {
    const template = renameDialog.template;
    if (!template || !newName.trim()) return;

    setIsProcessing(true);
    try {
      await updateTemplate(template.id, { name: newName.trim() });
      setTemplates(prev => prev.map(t => t.id === template.id ? { ...t, name: newName.trim() } : t));
      if (selectedTemplate?.id === template.id) {
        setSelectedTemplate(prev => prev ? { ...prev, name: newName.trim() } : null);
      }
      setStatusMessage({ type: 'success', message: 'Template renamed' });
      setRenameDialog({ isOpen: false, template: null });
    } catch (err) {
      setStatusMessage({ type: 'error', message: err.message });
    } finally {
      setIsProcessing(false);
    }
  }, [renameDialog.template, selectedTemplate]);

  const handleRenameCancel = useCallback(() => {
    setRenameDialog({ isOpen: false, template: null });
  }, []);

  const handlePushClick = useCallback(() => {
    if (!selectedAgent || !selectedTemplate) return;
    setPushPreview({ isOpen: true });
  }, [selectedAgent, selectedTemplate]);

  const handlePushConfirm = useCallback(async () => {
    if (!selectedAgent || !selectedTemplate) return;
    setIsProcessing(true);
    try {
      // Backend uses template.location automatically
      await pushToAgent(selectedAgent.id, selectedTemplate.id);
      const location = selectedTemplate.location || 'global';
      const locationLabel = LOCATION_OPTIONS[location]?.label || location;
      setStatusMessage({ type: 'success', message: `Pushed to ${selectedAgent.displayName || selectedAgent.name} (${locationLabel})` });
      setPushPreview({ isOpen: false });
    } catch (err) {
      setStatusMessage({ type: 'error', message: err.message });
    } finally {
      setIsProcessing(false);
    }
  }, [selectedAgent, selectedTemplate]);

  const handlePushCancel = useCallback(() => {
    setPushPreview({ isOpen: false });
  }, []);

  const handlePull = useCallback(async () => {
    if (!selectedAgent) return;
    setIsProcessing(true);
    try {
      // Import from agent creates a new template in DB
      const result = await importFromAgent(selectedAgent.id, pullLocation);
      // Reload templates to show the imported one
      await loadTemplates();
      if (result.data) {
        setSelectedTemplate(result.data);
      }
      const locationLabel = LOCATION_OPTIONS[pullLocation]?.label || pullLocation;
      setStatusMessage({ type: 'success', message: `Imported ${locationLabel} CLAUDE.md from ${selectedAgent.displayName || selectedAgent.name}` });
    } catch (err) {
      setStatusMessage({ type: 'error', message: err.message });
    } finally {
      setIsProcessing(false);
    }
  }, [selectedAgent, pullLocation, loadTemplates]);

  const handleCreateClick = useCallback((type) => {
    setCreateDialog({ isOpen: true, type });
  }, []);

  const handleCreateConfirm = useCallback(async (name, location) => {
    const type = createDialog.type;
    if (!type || !name) return;

    setIsProcessing(true);
    try {
      const result = await createTemplate({
        name: name.toLowerCase().replace(/\s+/g, '-'),
        type,
        content: `# ${name}\n\nNew ${type} template.`,
        parentId: type === 'base' ? null : templates.find(t => t.type === 'base')?.id || null,
        location: location || 'global',
      });
      setTemplates(prev => [...prev, result.data]);
      setSelectedTemplate(result.data);
      setStatusMessage({ type: 'success', message: 'Template created' });
      setCreateDialog({ isOpen: false, type: null, location: 'global' });
    } catch (err) {
      setStatusMessage({ type: 'error', message: err.message });
    } finally {
      setIsProcessing(false);
    }
  }, [createDialog.type, templates]);

  const handleCreateCancel = useCallback(() => {
    setCreateDialog({ isOpen: false, type: null, location: 'global' });
  }, []);

  const handleCreateLocationChange = useCallback((location) => {
    setCreateDialog(prev => ({ ...prev, location }));
  }, []);

  const handleParentChange = useCallback((parentId) => {
    if (!editedTemplate) return;
    setEditedTemplate(prev => ({
      ...prev,
      parentId
    }));
  }, [editedTemplate]);

  const handleBatchPushClick = useCallback(() => {
    if (!selectedTemplate) return;
    setBatchPush({ isOpen: true });
  }, [selectedTemplate]);

  const handleBatchPushClose = useCallback(() => {
    setBatchPush({ isOpen: false });
  }, []);

  const handleBatchPushComplete = useCallback((count) => {
    setStatusMessage({ type: 'success', message: `Pushed to ${count} agent${count > 1 ? 's' : ''} successfully` });
    setBatchPush({ isOpen: false });
  }, []);

  // Admin: Distribute Modal handlers
  const handleDistributeClick = useCallback(() => {
    if (!selectedTemplate) return;
    setDistributeModal({ isOpen: true });
  }, [selectedTemplate]);

  const handleDistributeClose = useCallback(() => {
    setDistributeModal({ isOpen: false });
  }, []);

  const handleDistributeComplete = useCallback((count) => {
    setStatusMessage({ type: 'success', message: `Distributed to ${count} user${count > 1 ? 's' : ''} successfully` });
    setDistributeModal({ isOpen: false });
  }, []);

  // Admin: Pull Modal handlers
  const handleAdminPullClick = useCallback(() => {
    setAdminPullModal({ isOpen: true });
  }, []);

  const handleAdminPullClose = useCallback(() => {
    setAdminPullModal({ isOpen: false });
  }, []);

  const handleAdminPullComplete = useCallback((templateName) => {
    setStatusMessage({ type: 'success', message: `Saved template "${templateName}"` });
    loadTemplates(activeTab); // Reload to show new template
    setAdminPullModal({ isOpen: false });
  }, [activeTab]);

  // Render
  return (
    <div className={`flex flex-col h-full bg-gray-900 ${className}`}>
      {/* Header */}
      <header className="flex-shrink-0 border-b border-gray-800">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <FileText className="w-6 h-6 text-indigo-400" />
            <h1 className="text-xl font-semibold text-white">CLAUDE.md Manager</h1>
          </div>

          <div className="flex items-center gap-3">
            <AgentSelector
              agents={agents}
              selectedAgent={selectedAgent}
              onSelect={setSelectedAgent}
              isLoading={isLoadingAgents}
            />

            {selectedAgent && (
              <>
                {/* Pull with Location Selector */}
                <div className="flex items-center">
                  <button
                    type="button"
                    onClick={handlePull}
                    disabled={isProcessing}
                    className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-200 bg-gray-700 hover:bg-gray-600 rounded-l-lg border-r border-gray-600 disabled:opacity-50 transition-colors"
                    title={`Pull ${pullLocation === 'project' ? 'Project' : 'Global'} CLAUDE.md from agent`}
                  >
                    <Download className="w-4 h-4" />
                    Pull
                  </button>
                  <div className="flex bg-gray-700 rounded-r-lg">
                    <button
                      type="button"
                      onClick={() => setPullLocation('global')}
                      disabled={isProcessing}
                      className={`w-9 h-9 flex items-center justify-center transition-colors disabled:opacity-50 ${
                        pullLocation === 'global'
                          ? 'text-sky-400 bg-sky-500/20'
                          : 'text-gray-400 hover:text-gray-200'
                      }`}
                      title="Pull from ~/.claude/CLAUDE.md"
                    >
                      <Home className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setPullLocation('project')}
                      disabled={isProcessing}
                      className={`w-9 h-9 flex items-center justify-center rounded-r-lg transition-colors disabled:opacity-50 ${
                        pullLocation === 'project'
                          ? 'text-orange-400 bg-orange-500/20'
                          : 'text-gray-400 hover:text-gray-200'
                      }`}
                      title="Pull from WORKING_DIR/.claude/CLAUDE.md"
                    >
                      <FolderCode className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handlePushClick}
                  disabled={isProcessing || !selectedTemplate}
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-500 rounded-lg disabled:opacity-50 transition-colors"
                  title="Push selected template to agent"
                >
                  <Upload className="w-4 h-4" />
                  Push
                </button>
              </>
            )}

            {/* Batch Push Button - always visible if template selected */}
            {selectedTemplate && (
              <button
                type="button"
                onClick={handleBatchPushClick}
                disabled={isProcessing}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-200 bg-gray-700 hover:bg-gray-600 rounded-lg disabled:opacity-50 transition-colors"
                title="Push to multiple agents"
              >
                <Users className="w-4 h-4" />
                Batch
              </button>
            )}

            {/* Pending Agent Edits Badge */}
            {pendingEditsCount > 0 && (
              <button
                type="button"
                onClick={handleEditReviewOpen}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-orange-300 bg-orange-500/15 hover:bg-orange-500/25 border border-orange-500/30 rounded-lg transition-colors"
                title={`${pendingEditsCount} pending agent edit${pendingEditsCount > 1 ? 's' : ''}`}
              >
                <Pencil className="w-4 h-4" />
                <span>{pendingEditsCount}</span>
              </button>
            )}

            {/* Admin Buttons - only visible for admins */}
            {isAdmin && (
              <>
                {selectedTemplate && (
                  <button
                    type="button"
                    onClick={handleDistributeClick}
                    disabled={isProcessing}
                    className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-200 bg-indigo-700 hover:bg-indigo-600 rounded-lg disabled:opacity-50 transition-colors"
                    title="Distribute to users"
                  >
                    <Share2 className="w-4 h-4" />
                    Distribute
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleAdminPullClick}
                  disabled={isProcessing}
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-200 bg-blue-700 hover:bg-blue-600 rounded-lg disabled:opacity-50 transition-colors"
                  title="Pull from any agent (Admin)"
                >
                  <Download className="w-4 h-4" />
                  Admin Pull
                </button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Status Messages */}
      {statusMessage && (
        <div className="px-6 pt-4">
          <StatusMessage
            type={statusMessage.type}
            message={statusMessage.message}
            onDismiss={() => setStatusMessage(null)}
          />
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar - Template List */}
        <div className="w-96 flex-shrink-0 border-r border-gray-800 flex flex-col">
          {/* Tab Navigation */}
          <TemplateTabs
            activeTab={activeTab}
            onTabChange={handleTabChange}
            isAdmin={isAdmin}
            templateCounts={templateCounts}
            unreadChangesCount={unreadChangesCount}
          />

          {activeTab === 'changes' ? (
            /* Changes Tab Content */
            <ChangesList
              changes={recentChanges}
              isLoading={isLoadingChanges}
              onMarkAllRead={handleMarkAllRead}
              onSelectChange={handleSelectChange}
              isMarkingRead={isMarkingRead}
            />
          ) : (
            <>
              {/* Search + Actions */}
              <div className="p-4 border-b border-gray-800">
                <div className="relative mb-3">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search templates..."
                    className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                {/* Create New Buttons - hidden on global tab for non-admins */}
                {(!isCurrentTabReadOnly || isAdmin) && (
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => handleCreateClick('base')}
                      disabled={isProcessing || (activeTab === 'global' && !isAdmin)}
                      className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium text-blue-400 bg-blue-500/10 hover:bg-blue-500/20 rounded transition-colors disabled:opacity-50"
                    >
                      <Plus className="w-3 h-3" />
                      Base
                    </button>
                    <button
                      type="button"
                      onClick={() => handleCreateClick('role')}
                      disabled={isProcessing || (activeTab === 'global' && !isAdmin)}
                      className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium text-green-400 bg-green-500/10 hover:bg-green-500/20 rounded transition-colors disabled:opacity-50"
                    >
                      <Plus className="w-3 h-3" />
                      Role
                    </button>
                    <button
                      type="button"
                      onClick={() => handleCreateClick('agent')}
                      disabled={isProcessing || (activeTab === 'global' && !isAdmin)}
                      className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium text-purple-400 bg-purple-500/10 hover:bg-purple-500/20 rounded transition-colors disabled:opacity-50"
                    >
                      <Plus className="w-3 h-3" />
                      Agent
                    </button>
                  </div>
                )}

                {/* Read-only notice for global tab */}
                {isCurrentTabReadOnly && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-yellow-500/10 border border-yellow-500/30 rounded-lg text-xs text-yellow-400">
                    <Lock className="w-3 h-3" />
                    <span>Global templates are read-only. Contact an admin to edit.</span>
                  </div>
                )}
              </div>

              {/* Template List */}
              <div className="flex-1 overflow-y-auto p-4">
                {isLoadingTemplates ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 text-indigo-400 animate-spin" />
                  </div>
                ) : (
                  <TemplateList
                    templates={templates}
                    selectedTemplate={selectedTemplate}
                    onSelect={setSelectedTemplate}
                    onDelete={handleDeleteClick}
                    onRename={handleRenameClick}
                    searchQuery={searchQuery}
                    isProcessing={isProcessing}
                    isReadOnly={isCurrentTabReadOnly}
                    pendingEditsByTemplate={pendingEditsByTemplate}
                  />
                )}
              </div>
            </>
          )}
        </div>

        {/* Right Panel - Editor */}
        <div className="flex-1 flex flex-col min-w-0">
          {isLoadingChangeTemplate && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-indigo-400 animate-spin" />
            </div>
          )}
          {changeTemplateError && !isLoadingChangeTemplate && (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <AlertCircle className="w-8 h-8 mb-2 text-red-400" />
              <p className="text-sm text-red-400">{changeTemplateError}</p>
            </div>
          )}
          {!isLoadingChangeTemplate && !changeTemplateError && (
            <TemplateEditor
              template={editedTemplate}
              templates={templates}
              onChange={setEditedTemplate}
              onSave={handleSave}
              onParentChange={handleParentChange}
              onVersionRollback={(restoredData) => {
                // After rollback: update selectedTemplate to sync state
                if (restoredData && selectedTemplate) {
                  const updated = { ...selectedTemplate, content: restoredData.content || selectedTemplate.content };
                  setSelectedTemplate(updated);
                  setTemplates(prev => prev.map(t => t.id === updated.id ? updated : t));
                }
              }}
              isSaving={isSaving}
              hasChanges={hasChanges}
            />
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title="Delete Template"
        itemName={deleteConfirm.template?.name || ''}
        warning="This action cannot be undone."
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        isProcessing={isProcessing}
      />

      {/* Rename Dialog */}
      <InputDialog
        isOpen={renameDialog.isOpen}
        title="Rename Template"
        label="New name"
        placeholder="template-name"
        defaultValue={renameDialog.template?.name || ''}
        onConfirm={handleRenameConfirm}
        onCancel={handleRenameCancel}
        isProcessing={isProcessing}
      />

      {/* Create Template Dialog */}
      <CreateTemplateDialog
        isOpen={createDialog.isOpen}
        type={createDialog.type}
        location={createDialog.location}
        onLocationChange={handleCreateLocationChange}
        onConfirm={handleCreateConfirm}
        onCancel={handleCreateCancel}
        isProcessing={isProcessing}
      />

      {/* Push Preview Modal */}
      <PushPreviewModal
        isOpen={pushPreview.isOpen}
        template={selectedTemplate}
        agent={selectedAgent}
        location={selectedTemplate?.location || 'global'}
        onConfirm={handlePushConfirm}
        onCancel={handlePushCancel}
        isPushing={isProcessing}
      />

      {/* Batch Push Modal */}
      <BatchPushModal
        isOpen={batchPush.isOpen}
        template={selectedTemplate}
        agents={agents}
        onClose={handleBatchPushClose}
        onComplete={handleBatchPushComplete}
      />

      {/* Admin: Distribute Modal */}
      <DistributeModal
        isOpen={distributeModal.isOpen}
        template={selectedTemplate}
        onClose={handleDistributeClose}
        onComplete={handleDistributeComplete}
      />

      {/* Admin: Pull Modal */}
      <AdminPullModal
        isOpen={adminPullModal.isOpen}
        onClose={handleAdminPullClose}
        onComplete={handleAdminPullComplete}
      />

      {/* Agent Edit Review Modal */}
      <AgentEditReviewModal
        isOpen={editReviewModal.isOpen}
        pendingEdits={pendingEdits}
        currentIndex={editReviewModal.currentIndex}
        onNavigate={handleEditReviewNavigate}
        onApprove={handleEditApprove}
        onReject={handleEditReject}
        onClose={handleEditReviewClose}
      />
    </div>
  );
}

// Named Exports
export {
  TemplateTypeBadge,
  LocationBadge,
  CreateTemplateDialog,
  AgentSelector,
  TemplateList,
  TemplateEditor,
  ParentSelector,
  StatusMessage,
  TemplateTabs,
  TEMPLATE_TYPE_COLORS,
  TEMPLATE_TABS,
  LOCATION_OPTIONS
};
